import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { usePageBreadcrumbs } from '../hooks/usePageBreadcrumbs';
import { resolveAdminPath } from '../utils/adminBreadcrumbUtils';
import { useGlobalModelConfiguration } from '../hooks/useGlobalModelConfiguration';
import { useApprovedModels } from '../hooks/useApprovedModels';
import GlobalModelConfigurationForm from '../components/modelConfiguration/GlobalModelConfigurationForm';
import ApprovedModelManagement from '../components/modelConfiguration/ApprovedModelManagement';

const ModelConfigurationPage = () => {
  const { hasPermission } = useAuth();
  const adminPath = resolveAdminPath(hasPermission);
  const adminSegmentPath = (adminPath && adminPath !== '/admin/model-configuration') ? adminPath : undefined;

  usePageBreadcrumbs([
    { label: 'Admin', path: adminSegmentPath },
    { label: 'Model Configuration' }
  ]);
  const [activeTab, setActiveTab] = useState(0);

  const {
    configuration,
    availableModels,
    loading: configLoading,
    saving,
    error: configError,
    successMessage: configSuccess,
    saveConfiguration,
    refreshAvailableModels,
    clearError: clearConfigError,
    clearSuccessMessage: clearConfigSuccess
  } = useGlobalModelConfiguration();

  const {
    models: approvedModels,
    loading: modelsLoading,
    error: modelsError,
    successMessage: modelsSuccess,
    createModel,
    updateModel,
    deactivateModel,
    deprecateModel,
    deleteModel,
    checkUsage,
    detectCapabilities,
    clearError: clearModelsError,
    clearSuccessMessage: clearModelsSuccess
  } = useApprovedModels();

  const isAdmin = hasPermission('permissions', 'manage');

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSaveConfiguration = async (data) => {
    try {
      await saveConfiguration(data);
    } catch (err) {
      // Error handled by hook
    }
  };

  const handleCreateModel = async (data) => {
    try {
      await createModel(data);
      // Refresh available models list so the new model appears in the browse dialog
      await refreshAvailableModels();
    } catch (err) {
      // Error handled by hook
    }
  };

  const handleUpdateModel = async (id, data) => {
    try {
      await updateModel(id, data);
      // Refresh available models list in case display name or other metadata changed
      await refreshAvailableModels();
    } catch (err) {
      // Error handled by hook
    }
  };

  const handleDeactivateModel = async (id) => {
    try {
      await deactivateModel(id);
      // Refresh available models list to remove deactivated model from selection
      await refreshAvailableModels();
    } catch (err) {
      // Error handled by hook
    }
  };

  const handleDeprecateModel = async (id) => {
    try {
      await deprecateModel(id);
      // Refresh available models list to remove deprecated model from selection
      await refreshAvailableModels();
    } catch (err) {
      // Error handled by hook
    }
  };

  const handleDeleteModel = async (id) => {
    try {
      await deleteModel(id);
      // Refresh available models list to remove deleted model from selection
      await refreshAvailableModels();
    } catch (err) {
      // Error handled by hook
    }
  };

  if (!isAdmin) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          You do not have permission to access this page. Admin access is required.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Model Configuration
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Global Configuration" />
          <Tab label="Approved Models" />
        </Tabs>
      </Box>

      <Box sx={{ mt: 3 }}>
        {activeTab === 0 && (
          configLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <GlobalModelConfigurationForm
              configuration={configuration}
              availableModels={availableModels}
              onSave={handleSaveConfiguration}
              onCancel={() => {}}
              saving={saving}
            />
          )
        )}

        {activeTab === 1 && (
          modelsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <ApprovedModelManagement
              models={approvedModels}
              onCreateModel={handleCreateModel}
              onUpdateModel={handleUpdateModel}
              onDeactivateModel={handleDeactivateModel}
              onDeprecateModel={handleDeprecateModel}
              onDeleteModel={handleDeleteModel}
              onCheckUsage={checkUsage}
              onDetectCapabilities={(model) => detectCapabilities(model.id)}
            />
          )
        )}
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={!!(configSuccess || modelsSuccess)}
        autoHideDuration={6000}
        onClose={() => {
          clearConfigSuccess();
          clearModelsSuccess();
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => {
          clearConfigSuccess();
          clearModelsSuccess();
        }} severity="success" sx={{ width: '100%' }}>
          {configSuccess || modelsSuccess}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!(configError || modelsError)}
        autoHideDuration={6000}
        onClose={() => {
          clearConfigError();
          clearModelsError();
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => {
          clearConfigError();
          clearModelsError();
        }} severity="error" sx={{ width: '100%' }}>
          {configError || modelsError}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ModelConfigurationPage;
